/**
 * Outbound Call Dialog
 *
 * Dialog for initiating outbound calls to leads using AI agents
 */

"use client"

import { FC, useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { IconLoader2, IconAlertTriangle } from "@tabler/icons-react"
import { Call2 } from "marshmallow-icons"
import { toast } from "sonner"
import { CRMLead, CRMContact, CRMCompany } from "@/types/nexus"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { demoDataService } from "@/lib/nexus/demo/demo-data-service"

interface OutboundCallDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contact?: CRMContact | CRMCompany | CRMLead
  workspaceId: string
  onSuccess?: () => void
  customPrompt?: string
}

interface Agent {
  id: string
  name: string
  type: string
  description: string
}

export const OutboundCallDialog: FC<OutboundCallDialogProps> = ({
  open,
  onOpenChange,
  contact,
  workspaceId,
  onSuccess,
  customPrompt
}) => {
  const [agents, setAgents] = useState<Agent[]>([])
  const [selectedAgentId, setSelectedAgentId] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [loadingAgents, setLoadingAgents] = useState(true)

  // Normalize contact data for different entity types
  const normalizedContact = contact ? {
    id: contact.id,
    first_name: 'first_name' in contact ? contact.first_name : (contact as CRMCompany).name.split(' ')[0] || '',
    last_name: 'last_name' in contact ? contact.last_name : (contact as CRMCompany).name.split(' ').slice(1).join(' ') || '',
    phone: contact.phone || '',
    type: 'type' in contact ? contact.type : 'company'
  } : null

  useEffect(() => {
    if (open && workspaceId) {
      loadAgents()
    }
  }, [open, workspaceId])

  const loadAgents = async () => {
    console.log('[DEBUG] OutboundCallDialog loadAgents called, workspaceId:', workspaceId, 'open:', open)
    try {
      setLoadingAgents(true)
      
      // Check if we're in demo mode (demo pages or forced demo mode)
      const isDemoMode = typeof window !== 'undefined' && (
        window.location.pathname.includes('/nexus/demo') ||
        sessionStorage.getItem('force_demo_mode') === 'true'
      )

      if (isDemoMode) {
        // Load demo agents
        demoDataService.initialize()
        const demoAgents = demoDataService.getAgents()
        const voiceAgents = demoAgents.filter(a => a.type === 'voice' || a.type === 'hybrid')
        const formattedAgents: Agent[] = voiceAgents.map(a => ({
          id: a.id,
          name: a.name,
          type: a.type,
          description: a.metadata?.description || ''
        }))
        setAgents(formattedAgents)
        if (formattedAgents.length > 0) {
          setSelectedAgentId(formattedAgents[0].id)
        }
        setLoadingAgents(false)
        return
      }

      console.log('[DEBUG] Making API call to /api/crm/agents?workspace_id=' + workspaceId)
      const response = await fetch(`/api/crm/agents?workspace_id=${workspaceId}`)
      const data = await response.json()
      console.log('[DEBUG] API response:', data)

      if (data.success) {
        setAgents(data.agents)
        if (data.agents.length > 0) {
          setSelectedAgentId(data.agents[0].id)
        }
      }
    } catch (error) {
      console.error("Error loading agents:", error)
      toast.error("Failed to load agents")
    } finally {
      setLoadingAgents(false)
    }
  }

  const handleCall = async () => {
    if (!selectedAgentId) {
      toast.error("Please select an agent")
      return
    }

    if (!normalizedContact?.phone) {
      toast.error("Contact does not have a phone number")
      return
    }

    // Check if we're in demo mode (only on /nexus/demo pages)
    const isDemoMode = typeof window !== 'undefined' && 
      window.location.pathname.includes('/nexus/demo')

    if (isDemoMode) {
      // Simulate demo call
      setLoading(true)
      try {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        // Create demo conversation
        const contactId = normalizedContact.id
        const phoneNumber = normalizedContact.phone
        const conversation = demoDataService.createDemoCall(contactId, phoneNumber, customPrompt)
        
        toast.success("Demo call initiated successfully!")
        onSuccess?.()
        onOpenChange(false)
      } catch (error) {
        console.error("Error initiating demo call:", error)
        toast.error("Failed to initiate call")
      } finally {
        setLoading(false)
      }
      return
    }

    try {
      setLoading(true)
      const response = await fetch("/api/crm/outbound/call", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lead_id: normalizedContact!.id,
          agent_id: selectedAgentId,
          workspace_id: workspaceId,
          custom_prompt: customPrompt,
          // Include phone number for manual calls
          ...(normalizedContact!.id.startsWith('manual_') && {
            phone_number: normalizedContact!.phone
          })
        })
      })

      const data = await response.json()

      if (data.success) {
        const contactName = normalizedContact!.id.startsWith('manual_')
          ? normalizedContact!.phone
          : `${normalizedContact!.first_name} ${normalizedContact!.last_name}`.trim() || normalizedContact!.phone
        toast.success(`Call initiated to ${contactName}`)
        onSuccess?.()
        onOpenChange(false)
      } else {
        toast.error(data.error || "Failed to initiate call")
      }
    } catch (error) {
      console.error("Error initiating call:", error)
      toast.error("Failed to initiate call")
    } finally {
      setLoading(false)
    }
  }

  if (!contact || !open) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Call2 width={20} height={20}  />
            Initiate Outbound Call
          </DialogTitle>
          <DialogDescription>
            Call {normalizedContact?.first_name} {normalizedContact?.last_name} at {normalizedContact?.phone} using an AI agent
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!normalizedContact?.phone && (
            <Alert variant="destructive">
              <IconAlertTriangle className="size-4" />
              <AlertDescription>
                This contact does not have a phone number. Please add a phone number before making a call.
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label>Select AI Agent</Label>
            {loadingAgents ? (
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <IconLoader2 size={16} className="animate-spin" />
                Loading agents...
              </div>
            ) : agents.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No active voice agents available. Create an agent in Nexus to make calls.
              </p>
            ) : (
              <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{agent.name}</span>
                        <span className="text-muted-foreground text-xs">{agent.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleCall} disabled={loading || !selectedAgentId || agents.length === 0}>
              {loading ? (
                <>
                  <IconLoader2 size={16} className="mr-2 animate-spin" />
                  Initiating...
                </>
              ) : (
                <>
                  <Call2 width={16} height={16}  />
                  Call Lead
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

